/**
 * VireoHR - Owner Schedules Screen
 * Ultra-Minimal Design with Full CRUD + Conflict Detection
 */

import { deleteDoc, doc } from 'firebase/firestore';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useScheduleData } from '@/hooks/useScheduleData';
import { firestore } from '@/lib/firebase/client';
import { createSchedule } from '@/services/cloudFunctions';


export default function OwnerSchedules() {
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Use shared hook for schedule data
  const { schedules, branches, employees, loading, refetch } = useScheduleData({
    tenantId: user?.tenantId || '',
    role: 'owner',
    filterFutureOnly: true
  });
  
  // Form state
  const [formData, setFormData] = useState({
    branchId: '',
    employeeIds: [] as string[],
    date: '',
    startTime: '',
    endTime: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.tenantId || !formData.branchId || formData.employeeIds.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const loadingToast = toast.loading('Creating schedule...');
      
      // Combine date and time
      const startDateTime = new Date(`${formData.date}T${formData.startTime}:00`);
      const endDateTime = new Date(`${formData.date}T${formData.endTime}:00`);

      const result = await createSchedule({
        tenantId: user.tenantId,
        branchId: formData.branchId,
        employeeIds: formData.employeeIds,
        startTimestamp: startDateTime.toISOString(),
        endTimestamp: endDateTime.toISOString(),
      });

      toast.dismiss(loadingToast);

      if (result.data.success) {
        toast.success('Schedule created successfully');
        setShowCreateForm(false);
        resetForm();
        refetch();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create schedule');
    }
  };

  const handleDelete = async (scheduleId: string) => {
    if (!confirm('Delete this schedule?')) return;

    try {
      await deleteDoc(doc(firestore, 'schedules', scheduleId));
      toast.success('Schedule deleted');
      refetch();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const resetForm = () => {
    setFormData({
      branchId: '',
      employeeIds: [],
      date: '',
      startTime: '',
      endTime: '',
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout title="Schedules">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Schedules">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold text-gray-900">Schedules</h1>
          <Button onClick={() => setShowCreateForm(true)} variant="primary">
            Create Schedule
          </Button>
        </div>

        {schedules.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No upcoming schedules</p>
              <p className="text-xs text-gray-400 mt-1">Create your first schedule</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {schedules.map((schedule) => (
              <Card key={schedule.id}>
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {formatDateTime(schedule.startTimestamp)}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Ends: {new Date(schedule.endTimestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleDelete(schedule.id)}
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50 text-xs px-2 py-1"
                    >
                      Delete
                    </Button>
                  </div>
                  
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="text-sm text-gray-900 mt-0.5">{schedule.branchName || 'Unknown'}</p>
                  </div>
                  
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Assigned Employees</p>
                    <div className="flex flex-wrap gap-1">
                      {schedule.employeeNames?.map((name, idx) => (
                        <Badge key={idx} variant="default">{name}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Schedule Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <Card className="max-w-md w-full my-8">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Create Schedule</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <select
                  value={formData.branchId}
                  onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                  required
                >
                  <option value="">Select location</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                  required
                />
              </div>

              {/* Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                    required
                  />
                </div>
              </div>

              {/* Employees */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assign Employees *</label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded p-3">
                  {employees.map((employee) => (
                    <label key={employee.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.employeeIds.includes(employee.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              employeeIds: [...formData.employeeIds, employee.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              employeeIds: formData.employeeIds.filter(id => id !== employee.id)
                            });
                          }
                        }}
                        className="w-4 h-4 text-[#0D9488] border-gray-300 rounded focus:ring-[#0D9488]"
                      />
                      <span className="text-sm text-gray-700">{employee.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1" variant="primary">
                  Create Schedule
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </Layout>
  );
}
